import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToCleaningHostRequests,
  CleaningRequest,
} from "../../../../redux/cleaningReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import { listenToCleaningData } from "../../../../redux/cleaningSlice";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../../firebase";
import {
  Check, X, Clock, Calendar, MapPin, ChevronDown, ChevronUp,
} from "lucide-react";

/* ── green tokens ── */
const G = {
  bg:          "#f0fdf4",
  surface:     "#ffffff",
  border:      "#bbf7d0",
  border2:     "#86efac",
  text:        "#14532d",
  textSoft:    "#166534",
  muted:       "#6b7280",
  green:       "#16a34a",
  greenDark:   "#14532d",
  greenLight:  "#dcfce7",
  greenBorder: "#86efac",
  gold:        "#ca8a04",
  goldLight:   "#fef9c3",
  goldBorder:  "#fde68a",
  red:         "#dc2626",
  redLight:    "#fee2e2",
  redBorder:   "#fca5a5",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }
`;

/* ── date helpers ── */
const parseISODate = (iso: string): Date => {
  if (!iso) return new Date();
  const clean = iso.split("T")[0];
  const [y, m, d] = clean.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (iso: string) =>
  parseISODate(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

const diffDays = (a: string, b: string) => {
  const ms = parseISODate(b).getTime() - parseISODate(a).getTime();
  return Math.round(ms / 86400000);
};

/* ── approve / decline ── */
const approveRequest = async (requestId: string) => {
  await updateDoc(doc(db, "CleaningRequests", requestId), {
    isPending: false, isApproved: true, isRejected: false,
    updatedAt: serverTimestamp(),
  });
};

const declineRequest = async (requestId: string) => {
  await updateDoc(doc(db, "CleaningRequests", requestId), {
    isPending: false, isApproved: false, isRejected: true,
    updatedAt: serverTimestamp(),
  });
};

/* ── shared badge ── */
const badge: React.CSSProperties = {
  display: "inline-flex", alignItems: "center",
  padding: "3px 9px", borderRadius: 20,
  fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", whiteSpace: "nowrap",
};

/* ════════════════════════════════════════════════════════════ */
const CleaningRequests: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const user       = useSelector((s: RootState) => s.auth.user);
  const userId     = user?.userId || (user as any)?.uid || null;
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);

  const requests  = useSelector((s: RootState) => s.cleaningReservations.hostReservationRequests);
  const allUsers  = useSelector((s: RootState) => s.auth.allUsers);
  const listings  = useSelector((s: RootState) => s.cleaning.listings);

  const [expanded,        setExpanded]        = useState<string | null>(null);
  const [loadingId,       setLoadingId]       = useState<string | null>(null);
  const [fetchedListings, setFetchedListings] = useState<string[]>([]);
  const [tab,             setTab]             = useState<"pending" | "approved" | "declined">("pending");

  /* ── fetch ── */
  useEffect(() => {
    if (authLoaded && userId) {
      dispatch(listenToCleaningHostRequests(userId));
      dispatch(fetchAllUsers());
    }
  }, [authLoaded, userId, dispatch]);

  useEffect(() => {
    requests.forEach((req) => {
      if (!fetchedListings.includes(req.listingId)) {
        dispatch(listenToCleaningData(req.listingId));
        setFetchedListings((p) => [...p, req.listingId]);
      }
    });
  }, [requests, fetchedListings, dispatch]);

  /* ── helpers ── */
  const getGuest   = (id: string) => allUsers.find((u: any) => u.userId === id);
  const getListing = (id: string) => listings.find((l: any) => l.listingId === id);

  const filtered = requests.filter((r) => {
    if (tab === "pending")  return r.isPending && !r.isApproved && !r.isRejected;
    if (tab === "approved") return r.isApproved;
    if (tab === "declined") return r.isRejected;
    return false;
  });

  const pendingCount = requests.filter(
    (r) => r.isPending && !r.isApproved && !r.isRejected
  ).length;

  /* ── action handlers ── */
  const handleApprove = async (req: CleaningRequest) => {
    setLoadingId(req.requestId);
    try { await approveRequest(req.requestId); }
    catch (e) { console.error(e); }
    finally   { setLoadingId(null); }
  };

  const handleDecline = async (req: CleaningRequest) => {
    setLoadingId(req.requestId);
    try { await declineRequest(req.requestId); }
    catch (e) { console.error(e); }
    finally   { setLoadingId(null); }
  };

  /* ── status badge ── */
  const StatusBadge = ({ req }: { req: CleaningRequest }) => {
    if (req.isApproved)
      return <span style={{ ...badge, background: G.greenLight, color: G.green,   border: `1px solid ${G.greenBorder}` }}>Approved</span>;
    if (req.isRejected)
      return <span style={{ ...badge, background: G.redLight,   color: G.red,     border: `1px solid ${G.redBorder}`   }}>Declined</span>;
    return   <span style={{ ...badge, background: G.goldLight,  color: G.gold,    border: `1px solid ${G.goldBorder}`  }}>Pending</span>;
  };

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ background: G.bg, minHeight: "100vh", fontFamily: G.sans, color: G.text, padding: "0 0 80px" }}>
      <style>{FONT_CSS}</style>

      {/* ── heading ── */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 10, fontWeight: 500, color: G.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>
          Cleaning dashboard
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 22, fontWeight: 400, fontFamily: G.serif, color: G.text }}>
            Booking requests
          </h2>
          {pendingCount > 0 && (
            <span style={{ ...badge, background: G.goldLight, color: G.gold, border: `1px solid ${G.goldBorder}`, fontSize: 11 }}>
              {pendingCount} pending
            </span>
          )}
        </div>
      </div>

      {/* ── tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {(["pending", "approved", "declined"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "7px 16px", borderRadius: 20, border: "none",
              fontFamily: G.sans, fontSize: 12, fontWeight: 500, cursor: "pointer",
              transition: "all .15s",
              background: tab === t ? G.greenDark : G.surface,
              color:      tab === t ? "#fff"      : G.muted,
              boxShadow:  tab === t ? "none"      : `0 0 0 1px ${G.border}`,
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "pending" && pendingCount > 0 && (
              <span style={{ marginLeft: 6, background: G.green, color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── empty state ── */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 20px", color: G.muted }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🧹</div>
          <p style={{ fontSize: 14 }}>No {tab} requests</p>
        </div>
      )}

      {/* ── request cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((req) => {
          const guest   = getGuest(req.guestId);
          const listing = getListing(req.listingId);
          const days    = diffDays(req.checkIn, req.checkOut);
          const isOpen  = expanded === req.requestId;
          const busy    = loadingId === req.requestId;

          return (
            <div
              key={req.requestId}
              style={{
                background: G.surface,
                border: `1px solid ${G.border}`,
                borderRadius: 14,
                overflow: "hidden",
              }}
            >
              {/* ── card header ── */}
              <div
                onClick={() => setExpanded(isOpen ? null : req.requestId)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer" }}
              >
                {/* listing photo */}
                <div style={{ width: 54, height: 54, borderRadius: 10, flexShrink: 0, background: G.greenLight, overflow: "hidden", border: `1px solid ${G.border}` }}>
                  {listing?.photos?.[0] ? (
                    <img src={listing.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🧹</div>
                  )}
                </div>

                {/* guest + info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    {guest?.profilePicture ? (
                      <img
                        src={guest.profilePicture} alt=""
                        onClick={(e) => { e.stopPropagation(); navigate(`/aboutme/${req.guestId}`); }}
                        style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", cursor: "pointer", flexShrink: 0, border: `2px solid ${G.green}` }}
                      />
                    ) : (
                      <div
                        onClick={(e) => { e.stopPropagation(); navigate(`/aboutme/${req.guestId}`); }}
                        style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                      >
                        {(guest?.firstName?.[0] || "C").toUpperCase()}
                      </div>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {guest ? `${guest.firstName} ${guest.lastName || ""}` : "Client"}
                    </span>
                    <StatusBadge req={req} />
                  </div>

                  {/* dates */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: G.muted }}>
                      <Calendar size={11} /><span>{formatDate(req.checkIn)}</span>
                    </div>
                    <span style={{ fontSize: 11, color: G.border2 }}>→</span>
                    <span style={{ fontSize: 11, color: G.muted }}>{formatDate(req.checkOut)}</span>
                    <span style={{ fontSize: 11, color: G.border2 }}>·</span>
                    <span style={{ fontSize: 11, color: G.muted }}>{days} day{days !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                <div style={{ color: G.muted, flexShrink: 0 }}>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* ── expanded ── */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${G.border}`, padding: "16px 16px 18px", background: G.bg }}>

                  {/* listing card */}
                  {listing && (
                    <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: G.text, marginBottom: 2 }}>
                        {listing.name || listing.title || "Cleaning Service"}
                      </p>
                      {listing.address && (
                        <p style={{ fontSize: 11, color: G.muted, display: "flex", alignItems: "center", gap: 4 }}>
                          <MapPin size={10} />
                          {[listing.address.street, listing.address.city, listing.address.country].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  )}

                  {/* detail rows */}
                  {[
                    { label: "Service Date",  val: formatDate(req.checkIn)  },
                    { label: "End Date",       val: formatDate(req.checkOut) },
                    { label: "Duration",       val: `${days} day${days !== 1 ? "s" : ""}` },
                    { label: "Total Hours",    val: req.totalHours ? `${req.totalHours}h` : "—" },
                    { label: "Rate Type",      val: req.rateType || "—" },
                    { label: "Request ID",     val: req.requestId.slice(0, 10) + "…" },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${G.border}` }}>
                      <span style={{ fontSize: 12, color: G.muted }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: G.text }}>{val}</span>
                    </div>
                  ))}

                  {/* view guest profile */}
                  <button
                    onClick={() => navigate(`/aboutme/${req.guestId}`)}
                    style={{ marginTop: 14, width: "100%", padding: "9px 0", borderRadius: 8, border: `1px solid ${G.border}`, background: G.surface, color: G.text, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: G.sans, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
                  >
                    {guest?.profilePicture ? (
                      <img src={guest.profilePicture} style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover" }} alt="" />
                    ) : (
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: `linear-gradient(135deg, ${G.green}, ${G.greenDark})`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                        {(guest?.firstName?.[0] || "C").toUpperCase()}
                      </div>
                    )}
                    View {guest?.firstName || "client"}'s profile
                  </button>

                  {/* message */}
                  <button
                    onClick={() => navigate(`/imessages/${req.guestId}`)}
                    style={{ marginTop: 8, width: "100%", padding: "9px 0", borderRadius: 8, border: `1px solid ${G.border2}`, background: "transparent", color: G.textSoft, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: G.sans }}
                  >
                    Message {guest?.firstName || "client"}
                  </button>

                  {/* approve / decline — pending only */}
                  {req.isPending && !req.isApproved && !req.isRejected && (
                    <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                      <button
                        onClick={() => handleDecline(req)}
                        disabled={busy}
                        style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: `1.5px solid ${G.redBorder}`, background: busy ? G.border : G.redLight, color: G.red, fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontFamily: G.sans, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: busy ? 0.6 : 1 }}
                      >
                        <X size={14} />{busy ? "…" : "Decline"}
                      </button>
                      <button
                        onClick={() => handleApprove(req)}
                        disabled={busy}
                        style={{ flex: 2, padding: "11px 0", borderRadius: 8, border: "none", background: busy ? G.greenLight : G.greenDark, color: "#fff", fontSize: 13, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", fontFamily: G.sans, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: busy ? 0.6 : 1 }}
                      >
                        <Check size={14} />{busy ? "Processing…" : "Approve booking"}
                      </button>
                    </div>
                  )}

                  {/* approved */}
                  {req.isApproved && (
                    <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: G.greenLight, border: `1px solid ${G.greenBorder}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: G.green, fontWeight: 500 }}>
                      <Check size={14} />
                      You approved this request. The client can now proceed to payment.
                    </div>
                  )}

                  {/* declined */}
                  {req.isRejected && (
                    <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 8, background: G.redLight, border: `1px solid ${G.redBorder}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: G.red, fontWeight: 500 }}>
                      <X size={14} />
                      You declined this request.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CleaningRequests;
